import { useCallback } from "react";

/**
 * Hook for handling common validations and error navigation
 * @param {Object} formMethods - react-hook-form methods
 * @returns {Object} Utility functions for validation
 */
function useFormValidation(formMethods) {
  const { trigger, setError, clearErrors, formState } = formMethods;

  /**
   * Focuses and scrolls to the first field with error
   */
  const focusFirstError = useCallback(() => {
    const { errors } = formState;
    const firstErrorField = Object.keys(errors)[0];

    if (firstErrorField) {
      const field = document.querySelector(`[name="${firstErrorField}"]`);
      if (field) {
        field.focus({ preventScroll: true });
        field.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [formState]);

  /**
   * Validates specific fields and focuses the first error
   * @param {string[]} fieldNames - Field names to validate
   * @param {boolean} shouldFocus - Whether to focus the first error
   * @returns {Promise<boolean>} Whether validation was successful
   */
  const validateFields = useCallback(
    async (fieldNames, shouldFocus = true) => {
      const isValid = await trigger(fieldNames, { shouldFocus });

      if (!isValid && shouldFocus) {
        focusFirstError();
      }

      return isValid;
    },
    [trigger, focusFirstError]
  );

  /**
   * Maps backend errors to form fields
   * @param {Object} backendErrors - Server errors
   */
  const mapBackendErrors = useCallback(
    (backendErrors) => {
      const processErrors = (errors, prefix = "") => {
        Object.entries(errors).forEach(([key, value]) => {
          const fieldName = prefix ? `${prefix}.${key}` : key;

          if (key === "non_field_errors") {
            // General form error
            setError("root", {
              type: "server",
              message: Array.isArray(value) ? value[0] : String(value),
            });
          } else if (Array.isArray(value)) {
            // Array of error messages
            setError(fieldName, {
              type: "server",
              message: value[0],
            });
          } else if (typeof value === "object" && value !== null) {
            // Nested object - recurse
            processErrors(value, fieldName);
          } else {
            // Simple string error
            setError(fieldName, {
              type: "server",
              message: String(value),
            });
          }
        });
      };

      processErrors(backendErrors);
    },
    [setError]
  );

  /**
   * Clears errors from specific fields
   * @param {string[]} fieldNames - Field names to clear
   */
  const clearFieldErrors = useCallback(
    (fieldNames) => {
      fieldNames.forEach((fieldName) => {
        clearErrors(fieldName);
      });
    },
    [clearErrors]
  );

  return {
    validateFields,
    focusFirstError,
    mapBackendErrors,
    clearFieldErrors,
  };
}

export default useFormValidation;
